import { Box, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { ssoKeys } from "../../../../api/queryKeys";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import CustomToolTip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { defaultStaleTime } from "../../../../utils/constants";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";

const SSOConfiguration = () => {
  const [isTicketUrlGenerated, setIsTicketUrlGenerated] = useState(false);

  const SSOStrategyToImageName = {
    okta: "okta",
    waad: "microsoft_entra_id",
  };
  const SSOStrategyToIDPName = {
    "google-apps": "Google Workspace",
    "keycloak-samlp": "Keycloak",
    adfs: "Microsoft Active Directory Federation Services",
    pingfederate: "PingFederate",
    oidc: "generic OIDC",
    samlp: "generic SAML",
  };

  const {
    trackEvent,
    isSsoIntegrated,
    generateSelfServiceSSOTicket,
    getOktaInitiateLoginURI,
  } = useAPI();

  const {
    data: configuredSSO,
    isLoading: configuredSSOIsLoading,
    refetch,
  } = useQuery({
    queryKey: ssoKeys.isIntegrated(),
    queryFn: isSsoIntegrated,
    select: (response) => response.data,
    // If the ticket was generated poll for fresh data
    staleTime: isTicketUrlGenerated ? 0 : defaultStaleTime,
    // Poll every 10 seconds if ticket is generated (i.e. waiting for the user to complete SSO setup)
    refetchInterval: isTicketUrlGenerated ? 10 * 1000 : false,
    refetchIntervalInBackground: true,
  });

  const { data: oktaInitiateLoginURI } = useQuery({
    queryKey: ssoKeys.oktaInitiateLoginURI(),
    queryFn: async () => {
      const response = await getOktaInitiateLoginURI();
      return response?.data?.initiate_login_uri || null;
    },
    enabled: configuredSSO === "okta",
  });

  if (configuredSSO) {
    // Stop polling if configuration is complete
    if (isTicketUrlGenerated) {
      setIsTicketUrlGenerated(false);
    }
  }

  const handleSSOSetupClick = () => {
    trackEvent("User clicked on setup SSO.");
    generateSelfServiceSSOTicket()
      .then((response) => {
        if (response?.data) {
          const ticketLink = response.data;
          window.open(ticketLink, "_blank");
          setIsTicketUrlGenerated(true);
          refetch();
        } else {
          Toast.error("Failed to generate SSO ticket. Please contact support.");
        }
      })
      .catch(() => {
        Toast.error("Failed to generate SSO ticket. Please contact support.");
      });
  };

  const renderContent = () => {
    if (configuredSSOIsLoading) {
      return (
        <Box mt={"16px"} display="flex" alignItems="flex-start">
          <LoadingIcon size={25} />
        </Box>
      );
    }

    if (configuredSSO) {
      return (
        <Box mt={"16px"} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {SSOStrategyToImageName[configuredSSO] ? (
              <Box
                gap={1}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <IntegrationImage
                  name={SSOStrategyToImageName[configuredSSO]}
                  width={"26px"}
                  newLogo={true}
                />
                <Typography variant="body2">SSO configured</Typography>
              </Box>
            ) : (
              SSOStrategyToIDPName[configuredSSO] && (
                <Box
                  gap={1}
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <Typography variant="body2">
                    {SSOStrategyToIDPName[configuredSSO]} SSO configured
                  </Typography>
                </Box>
              )
            )}
          </Box>

          {configuredSSO === "okta" && oktaInitiateLoginURI && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Optional: Open Breeze via the Okta app
              </Typography>
              <Typography variant="body2">
                Copy the following link and paste it in your Okta Breeze app
                under
                <strong> General Settings</strong> &gt;{" "}
                <strong>Initiate login URI </strong>.
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <CustomToolTip title={oktaInitiateLoginURI} placement="top">
                  <TextField
                    size="small"
                    value={oktaInitiateLoginURI}
                    InputProps={{ readOnly: true }}
                    sx={{
                      width: "800px",
                    }}
                  />
                </CustomToolTip>
                <CustomCopyButton
                  copyTitle="Initiate Login URI"
                  copyText={oktaInitiateLoginURI}
                  label="Copy"
                  sx={{ alignSelf: "center" }}
                />
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box mt={"16px"}>
        <Typography variant="body2">
          Set up SSO with your preferred identity provider
        </Typography>
        <PrimaryButton
          onClick={handleSSOSetupClick}
          size="small"
          sx={{ mt: 2 }}
        >
          Get Started
        </PrimaryButton>
        {isTicketUrlGenerated && (
          <Typography mt={2} variant="body2">
            Waiting for SSO configuration to complete...
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <div>
      <Box ml={"16px"} display="flex" flexDirection="column" gap={1}>
        <Typography variant="subtitle1">SSO Configuration</Typography>
      </Box>
      <Box ml={"16px"} display="flex" flexDirection="column" gap={1}>
        {renderContent()}
      </Box>
    </div>
  );
};

export default SSOConfiguration;
